import { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import type { FeatureStatus, Permission, PermissionCode } from '../../types';
import type { AdminRole } from '../../types/admin';
import { rolesApi } from '../../api/adminApi';
import { getApiErrorMessage } from '../../api/client';
import { notify } from '../../components/Notifier';
import { PERMISSION_GROUPS, PERMISSION_LABELS } from './permissionMeta';
import { FEATURE_STATUS_META, FEATURE_STATUS_OPTIONS } from './statusMeta';

interface RolePermissionDialogProps {
  open: boolean;
  role: AdminRole | null;
  allPermissions: Permission[];
  onClose: () => void;
  onSaved: () => void;
}

/** Hộp thoại gán quyền cho vai trò — checklist quyền theo nhóm nghiệp vụ. */
export function RolePermissionDialog({
  open,
  role,
  allPermissions,
  onClose,
  onSaved,
}: RolePermissionDialogProps): JSX.Element {
  const [selected, setSelected] = useState<Set<PermissionCode>>(new Set());
  const [statusFilter, setStatusFilter] = useState<Set<FeatureStatus>>(new Set());
  const [initialStatusFilter, setInitialStatusFilter] = useState<FeatureStatus[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !role) return;
    const initial = new Set((role.permissions ?? []).map((p) => p.code));
    setSelected(initial);
    setStatusFilter(new Set());
    setInitialStatusFilter([]);
    void rolesApi
      .getFeatureStatusFilter(role.id)
      .then((statuses) => {
        setStatusFilter(new Set(statuses));
        setInitialStatusFilter(statuses);
      })
      .catch(() => {
        // Giữ mặc định "xem tất cả" nếu API lỗi — không chặn thao tác phân quyền khác.
      });
  }, [open, role]);

  const availableCodes = useMemo(() => new Set(allPermissions.map((p) => p.code)), [allPermissions]);

  const extraGroup = useMemo(() => {
    const grouped = new Set(PERMISSION_GROUPS.flatMap((g) => g.codes));
    return allPermissions.filter((p) => !grouped.has(p.code));
  }, [allPermissions]);

  const toggle = (code: PermissionCode) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const toggleStatus = (status: FeatureStatus) => {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!role) return;
    setSaving(true);
    try {
      const previousCodes = (role.permissions ?? []).map((p) => p.code);
      const nextStatuses = Array.from(statusFilter);
      const statusFilterChanged =
        nextStatuses.length !== initialStatusFilter.length ||
        nextStatuses.some((s) => !initialStatusFilter.includes(s));
      await Promise.all([
        rolesApi.setPermissions(role.id, Array.from(selected), previousCodes),
        statusFilterChanged ? rolesApi.setFeatureStatusFilter(role.id, nextStatuses) : Promise.resolve(),
      ]);
      notify('Đã cập nhật quyền cho vai trò', 'success');
      onSaved();
      onClose();
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể cập nhật quyền'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Phân quyền: {role?.name}</DialogTitle>
      <DialogContent dividers>
        {PERMISSION_GROUPS.map((group) => {
          const codesInScope = group.codes.filter((c) => availableCodes.has(c));
          if (codesInScope.length === 0) return null;
          return (
            <Box key={group.title} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                {group.title}
              </Typography>
              <FormGroup>
                {codesInScope.map((code) => (
                  <FormControlLabel
                    key={code}
                    control={
                      <Checkbox
                        checked={selected.has(code)}
                        onChange={() => toggle(code)}
                        disabled={saving}
                        size="small"
                      />
                    }
                    label={PERMISSION_LABELS[code] ?? code}
                  />
                ))}
              </FormGroup>
              <Divider sx={{ mt: 1.5 }} />
            </Box>
          );
        })}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Tính năng — chỉ hiển thị theo trạng thái
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            Không chọn trạng thái nào = xem được tất cả (mặc định).
          </Typography>
          <FormGroup>
            {FEATURE_STATUS_OPTIONS.map((status) => (
              <FormControlLabel
                key={status}
                control={
                  <Checkbox
                    checked={statusFilter.has(status)}
                    onChange={() => toggleStatus(status)}
                    disabled={saving}
                    size="small"
                  />
                }
                label={FEATURE_STATUS_META[status].label}
              />
            ))}
          </FormGroup>
          <Divider sx={{ mt: 1.5 }} />
        </Box>
        {extraGroup.length > 0 && (
          <Box>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Khác
            </Typography>
            <FormGroup>
              {extraGroup.map((p) => (
                <FormControlLabel
                  key={p.code}
                  control={
                    <Checkbox
                      checked={selected.has(p.code)}
                      onChange={() => toggle(p.code)}
                      disabled={saving}
                      size="small"
                    />
                  }
                  label={p.description ?? p.code}
                />
              ))}
            </FormGroup>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Hủy
        </Button>
        <Button
          onClick={() => void handleSubmit()}
          variant="contained"
          disabled={saving || !role}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Lưu quyền
        </Button>
      </DialogActions>
    </Dialog>
  );
}
